import { useState } from 'react';

import {
  CardAction,
  type CardActionPayload,
  type CardItem,
  CardTimeline,
  type CardTimelineData,
  ClickableOrderNumber,
  type OnCardAction,
  OrderItemRows,
} from '../order-card/CardParts';
import { useCardPhrases } from '../order-card/CardLanguage';
import {
  asArray,
  formatCardDate,
  formatCardItemCount,
  formatCardPrice,
  formatOrderHeading,
  isLegacyListItem,
  returnSumLabel,
} from '../order-card/order-card-format';

export type OrderCardAction = CardActionPayload;

export type OrderCardItem = {
  kind?: 'order' | 'return';
  orderNumber: string;
  date?: string;
  placedAtIso?: string;
  itemCount: number;
  totalPrice?: number;
  currency?: string;
  products: string[];
  actions: OrderCardAction[];
  timeline?: CardTimelineData;
  footAction?: {
    label?: string;
    value?: string;
    actionId?: string;
    url?: string;
    quietLabel?: string;
  };
  returnScope?: 'full' | 'partial';
  statusLabel?: string;
  refundAmount?: number;
  items?: CardItem[];
};

// Äldre payloads utan kind (återupptagna historiska meddelanden) ritas som
// widgetens renderLegacyOrderCard.
function LegacyOrderCard({ order, onAction }: { order: OrderCardItem; onAction: OnCardAction }) {
  const phrases = useCardPhrases();
  const total = Number(order.totalPrice);
  const meta = [
    Number.isFinite(Number(order.itemCount))
      ? `${order.itemCount} ${order.itemCount === 1 ? 'artikel' : 'artiklar'}`
      : '',
    Number.isFinite(total)
      ? [
          new Intl.NumberFormat('sv-SE', {
            minimumFractionDigits: Number.isInteger(total) ? 0 : 2,
            maximumFractionDigits: Number.isInteger(total) ? 0 : 2,
          }).format(total),
          order.currency?.trim(),
        ]
          .filter(Boolean)
          .join(' ')
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const products = asArray<unknown>(order.products).map(String);
  const actions = asArray<OrderCardAction | null>(order.actions).filter(
    (action): action is OrderCardAction => Boolean(action?.label),
  );
  return (
    <div className="ai-chat__order-card">
      <div className="ai-chat__order-card-header">
        <div className="ai-chat__order-card-number">
          {formatOrderHeading(order.orderNumber, phrases)}
        </div>
        {order.date ? <div className="ai-chat__order-card-date">{order.date}</div> : null}
      </div>
      {meta ? <div className="ai-chat__order-card-meta">{meta}</div> : null}
      {products.length ? (
        <div className="ai-chat__order-card-products">
          {products.map((name, index) => (
            <div key={`${name}-${index}`}>{name}</div>
          ))}
        </div>
      ) : null}
      {actions.length ? (
        <div className="ai-chat__order-card-actions">
          {actions.map((action, index) => (
            <button
              key={`${action.label}-${index}`}
              type="button"
              className="suggested-action-btn"
              onClick={() => onAction(action)}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Samma kort som live-widgetens renderOrderCard: rubrik + tidslinje + fot.
// Ordrar jämförs inte — de skannas uppifrån och ner.
function OrderListCard({ order, onAction }: { order: OrderCardItem; onAction: OnCardAction }) {
  const phrases = useCardPhrases();
  const [itemsOpen, setItemsOpen] = useState(false);
  const isReturn = order.kind === 'return';
  const aside = isReturn
    ? order.returnScope === 'full'
      ? phrases.returnScopeFull
      : order.returnScope === 'partial'
        ? phrases.returnScopePartial
        : ''
    : formatCardDate(order.placedAtIso || order.date) || String(order.date ?? '');

  const total = Number(order.totalPrice);
  const sum = isReturn
    ? returnSumLabel(order.statusLabel, order.refundAmount, order.currency, phrases.locale)
    : [
        formatCardItemCount(order.itemCount, phrases),
        Number.isFinite(total) && total > 0
          ? formatCardPrice(total, order.currency, phrases.locale)
          : '',
      ]
        .filter(Boolean)
        .join(' · ');

  const hasItems = isReturn && asArray<CardItem | null>(order.items).some((item) => item?.name);

  return (
    <article className="ai-chat__card">
      <div className="ai-chat__card-head">
        <ClickableOrderNumber
          orderNumber={order.orderNumber}
          className="ai-chat__card-title"
          onAction={onAction}
        />
        {aside ? <div className="ai-chat__card-aside">{aside}</div> : null}
      </div>
      <CardTimeline timeline={order.timeline} />
      <div className="ai-chat__card-foot">
        <div className="ai-chat__card-sum">{sum}</div>
        {isReturn ? (
          hasItems ? (
            <button
              type="button"
              className="ai-chat__card-action"
              aria-expanded={itemsOpen}
              onClick={() => setItemsOpen((open) => !open)}
            >
              {itemsOpen ? phrases.hideItems : phrases.showItems}
            </button>
          ) : (
            <span className="ai-chat__card-quiet">{phrases.noUpdate}</span>
          )
        ) : (
          <CardAction
            {...(order.footAction ?? { quietLabel: phrases.noUpdate })}
            onAction={onAction}
          />
        )}
      </div>
      {hasItems ? (
        <div className="ai-chat__card-itemlist" hidden={!itemsOpen}>
          <OrderItemRows items={order.items} />
        </div>
      ) : null}
    </article>
  );
}

export function OrderCardList({
  orders,
  onActionClick,
}: {
  orders: OrderCardItem[];
  onActionClick: OnCardAction;
}) {
  const cards = orders.filter((order) => order?.orderNumber);
  if (cards.length === 0) return null;
  return (
    <div className="ai-chat__cards">
      {cards.map((order, index) =>
        isLegacyListItem(order) ? (
          <LegacyOrderCard
            key={`${order.orderNumber}-${index}`}
            order={order}
            onAction={onActionClick}
          />
        ) : (
          <OrderListCard
            key={`${order.orderNumber}-${index}`}
            order={order}
            onAction={onActionClick}
          />
        ),
      )}
    </div>
  );
}
